// File: frontend/src/components/ems/EventFilters.js

import React, { useState } from 'react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import Dropdown from '../common/Dropdown';
import styles from './EventFilters.module.css';

const EventFilters = ({ filters, onFilterChange, onApply, onReset, isLoading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const handleInputChange = (key, value) => {
    onFilterChange(key, value);
  };

  const handleApply = () => {
    onApply();
    setOpenDropdown(null);
    if (window.innerWidth < 768) {
      setIsExpanded(false);
    }
  };

  const handleReset = () => {
    onReset();
    setOpenDropdown(null);
    if (window.innerWidth < 768) {
      setIsExpanded(false);
    }
  };

  const handleDropdownOpenChange = (dropdownName, isOpen) => {
    if (isOpen) {
      setOpenDropdown(dropdownName);
    } else if (openDropdown === dropdownName) {
      setOpenDropdown(null);
    }
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'limit') return false;
    return value !== '' && value !== null && value !== undefined;
  }).length;

  const limitOptions = [
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];

  return (
    <div className={styles.filtersCard}>
      {/* Header - Always Visible */}
      <div className={styles.filtersHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.headerLeft}>
          <Filter size={20} />
          <h2 className={styles.filtersTitle}>Filters</h2>
          {activeFilterCount > 0 && (
            <span className={styles.filterCount}>{activeFilterCount}</span>
          )}
        </div>
        <button 
          type="button"
          className={styles.toggleButton}
          aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
        >
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {/* Filter Content - Collapsible on Mobile */}
      <div className={`${styles.filtersContent} ${isExpanded ? styles.expanded : ''}`}>
        <div className={styles.filtersGrid}>
          {/* Event Type */}
          <div className={styles.filterGroup}>
            <label htmlFor="filter-event-type" className={styles.filterLabel}>
              Event Type
            </label>
            <input
              id="filter-event-type"
              type="text"
              placeholder="e.g., USER_SIGNED_IN"
              value={filters.event_type}
              onChange={(e) => handleInputChange('event_type', e.target.value)}
              className={styles.filterInput}
              disabled={isLoading}
            />
          </div>
          
          {/* Aggregate ID */}
          <div className={styles.filterGroup}>
            <label htmlFor="filter-aggregate-id" className={styles.filterLabel}>
              Aggregate ID
            </label>
            <input
              id="filter-aggregate-id"
              type="text"
              placeholder="e.g., user#123"
              value={filters.aggregate_id}
              onChange={(e) => handleInputChange('aggregate_id', e.target.value)}
              className={styles.filterInput}
              disabled={isLoading}
            />
          </div>
          
          {/* Created By */}
          <div className={styles.filterGroup}>
            <label htmlFor="filter-created-by" className={styles.filterLabel}>
              Created By
            </label>
            <input
              id="filter-created-by"
              type="text"
              placeholder="e.g., user#123"
              value={filters.created_by}
              onChange={(e) => handleInputChange('created_by', e.target.value)}
              className={styles.filterInput}
              disabled={isLoading}
            />
          </div>

          {/* Start Date */}
          <div className={styles.filterGroup}>
            <label htmlFor="filter-start-date" className={styles.filterLabel}>
              Start Date
            </label>
            <input
              id="filter-start-date"
              type="date"
              value={filters.start_date}
              onChange={(e) => handleInputChange('start_date', e.target.value)}
              className={styles.filterInput}
              disabled={isLoading}
            />
          </div>

          {/* End Date */}
          <div className={styles.filterGroup}>
            <label htmlFor="filter-end-date" className={styles.filterLabel}>
              End Date
            </label>
            <input
              id="filter-end-date"
              type="date"
              value={filters.end_date}
              onChange={(e) => handleInputChange('end_date', e.target.value)}
              className={styles.filterInput}
              disabled={isLoading}
            />
          </div>

          {/* Limit */}
          <Dropdown
            label="Results Per Page"
            value={filters.limit}
            onChange={(value) => handleInputChange('limit', value)}
            options={limitOptions}
            disabled={isLoading}
            name="limit"
            isOpen={openDropdown === 'limit'}
            onOpenChange={(isOpen) => handleDropdownOpenChange('limit', isOpen)}
          />
        </div>

        {/* Actions */}
        <div className={styles.filterActions}>
          <button
            type="button"
            onClick={handleApply}
            disabled={isLoading}
            className={styles.applyButton}
          >
            {isLoading ? 'Applying...' : 'Apply Filters'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={isLoading}
            className={styles.resetButton}
          >
            <X size={16} />
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventFilters;